import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw } from 'lucide-react';

/**
 * App cache / service worker manual reset card. Rescues users from stale
 * PWA bundles on iOS Safari. Extracted from Settings.jsx (line 1213-1271).
 */
export function AppCacheCard({ language }) {
    const isDe = language === 'de';

    const clearCache = async () => {
        const confirmMsg = isDe
            ? 'App-Cache leeren und neu laden? Du bleibst eingeloggt — nur Cache-Bundles werden ersetzt.'
            : 'Clear app cache and reload? You stay logged in — only cached bundles are replaced.';
        if (!window.confirm(confirmMsg)) return;
        try {
            toast.info(isDe ? 'Lösche Cache…' : 'Clearing cache…');
            if ('caches' in window) {
                const names = await caches.keys();
                await Promise.all(names.map(n => caches.delete(n)));
            }
            if ('serviceWorker' in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map(r => r.unregister()));
            }
            window.location.replace(`${window.location.pathname}?cb=${Date.now()}`);
        } catch (err) {
            console.error('Cache clear failed:', err);
            toast.error(isDe ? 'Cache-Reset fehlgeschlagen' : 'Cache reset failed');
        }
    };

    return (
        <Card className="border-amber-200/40 dark:border-amber-800/30">
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-amber-600" />
                    {isDe ? 'App-Cache' : 'App cache'}
                </CardTitle>
                <CardDescription>
                    {isDe
                        ? 'Falls du die neuesten Features nicht siehst, leere hier den App-Cache. Deine Daten bleiben erhalten.'
                        : 'If you don\'t see the latest features, clear the app cache here. Your data is safe.'}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <p className="font-medium">
                            {isDe ? 'Cache leeren & neu laden' : 'Clear cache & reload'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            {isDe
                                ? 'Löscht Service-Worker + Cache und lädt die App frisch.'
                                : 'Wipes the service worker + cache and reloads the app fresh.'}
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={clearCache}
                        data-testid="clear-app-cache-btn"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {isDe ? 'Cache leeren' : 'Clear cache'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
